// =====================================================================
// /mes-conges - Concedii operator (Faza 3.6 Round 1)
// =====================================================================
// 3 secțiuni:
//   1. Mes soldes (7 pills: VA, VAN, VAEN1, AA, REC, MAT, PAT)
//   2. Nouvelle demande (form submit cu type/dates/reason)
//   3. Historique de mes demandes (listă cronologică cu status)
//
// Day 4 changes (20-05-2026):
//   - NEW prop `embedded` (default false) - cand este folosit ca tab interior
//     in pagina Conges, suprima propriul h1 si padding outer.
// =====================================================================

import { useState, useEffect } from 'react';
import { leaves as leavesApi, overtime as overtimeApi } from '../services/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import { useIsMobile } from '../hooks/useIsMobile.jsx';

const LEAVE_TYPE_LABELS = {
  VA: 'Vacances annuelles',
  VAN: 'Vacances année N+1',
  VAEN1: 'Vacances reportées (N-1)',
  AA: 'Absences autorisées',
  REC: 'Récupération',
  MAT: 'Maternité',
  PAT: 'Paternité',
  MAL: 'Maladie',
  'VA-0.5': 'Vacances (1/2j)',
  CC: 'Congé de circonstance',
  JFR: 'Férié de remplacement',
};

const STATUS_LABELS = {
  PENDING: 'En attente',
  APPROVED: 'Approuvée',
  REJECTED: 'Refusée',
  CANCELLED: 'Annulée',
};

const STATUS_COLORS = {
  PENDING: { bg: '#FEF3C7', fg: '#78350F', border: '#F59E0B' },
  APPROVED: { bg: '#DCFCE7', fg: '#166534', border: '#86EFAC' },
  REJECTED: { bg: '#FEE2E2', fg: '#991B1B', border: '#FCA5A5' },
  CANCELLED: { bg: '#F1F5F9', fg: '#64748B', border: '#CBD5E1' },
};

function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatDateFR(ymd) {
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
}

export default function MesConges({ embedded = false }) {
  const isMobile = useIsMobile(640);
  const { user } = useAuth();
  const [balance, setBalance] = useState(null);
  const [leavesList, setLeavesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state
  const [formType, setFormType] = useState('VA');
  const [formStart, setFormStart] = useState(todayYmd());
  const [formEnd, setFormEnd] = useState(todayYmd());
  const [formReason, setFormReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  // Day 11 — REC + AA variante: toggle jours/heures + solde OT (REC seulement)
  const [recMode, setRecMode] = useState('jours');
  const [recHours, setRecHours] = useState('');
  const [otBalance, setOtBalance] = useState(null);
  // Types qui supportent l'encodage en heures (partiel)
  const supportsHoursMode = formType === 'REC' || formType === 'AA';

  useEffect(() => {
    // Day 11++ (29-05-2026) — fix: backend renvoie `balanceHours` (pas balance/hours/totalHours).
    overtimeApi.getBalance().then(d => setOtBalance(d?.balanceHours ?? d?.balance ?? d?.hours ?? d?.totalHours ?? null)).catch(() => {});
  }, []);

  const refreshAll = async () => {
    setError(null);
    try {
      const [bal, list] = await Promise.all([
        leavesApi.getMyBalance(),
        leavesApi.listMe(),
      ]);
      setBalance(bal);
      setLeavesList(list);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refreshAll(); }, []);

  const handleSubmit = async () => {
    setFeedback(null);
    const isHoursMode = supportsHoursMode && recMode === 'heures';
    if (!isHoursMode && formStart > formEnd) {
      setFeedback({ type: 'error', message: 'La date de fin doit être supérieure ou égale à la date de début.' });
      return;
    }
    let partialDurationMin = null;
    if (isHoursMode) {
      const m = String(recHours).trim().match(/^(\d+):([0-5]?\d)$/);
      partialDurationMin = m ? (parseInt(m[1], 10) * 60 + parseInt(m[2], 10)) : NaN;
      if (isNaN(partialDurationMin) || partialDurationMin < 15 || partialDurationMin > 456) {
        setFeedback({ type: 'error', message: `Durée ${formType} invalide. Format hh:mm entre 0:15 et 7:36 (ex: 1:00).` });
        return;
      }
    }
    setSubmitting(true);
    try {
      const result = await leavesApi.submit({
        type: formType,
        startDate: formStart,
        endDate: isHoursMode ? formStart : formEnd,
        reason: formReason.trim() || null,
        ...(isHoursMode ? { partialDurationMin } : {}),
      });
      setFeedback({
        type: 'success',
        message: isHoursMode
          ? `Demande ${formType} en heures soumise: ${recHours} le ${formatDateFR(formStart)}. En attente de validation.`
          : `Demande soumise: ${LEAVE_TYPE_LABELS[formType]} du ${formatDateFR(formStart)} au ${formatDateFR(formEnd)} (${result.workdaysCount} jour(s) ouvré(s)). En attente de validation.`,
      });
      setFormReason(''); setRecHours('');
      await refreshAll();
    } catch (err) {
      const apiErr = err.response?.data;
      let msg = apiErr?.message || err.message || 'Erreur inconnue';
      if (apiErr?.error === 'PERIOD_LOCKED') msg = '🔒 ' + msg;
      else if (apiErr?.error === 'OVERLAP') msg = '⚠ ' + msg;
      setFeedback({ type: 'error', message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    if (!confirm('Êtes-vous sûr de vouloir annuler cette demande ?')) return;
    try {
      await leavesApi.cancel(id);
      await refreshAll();
    } catch (err) {
      alert('Erreur annulation: ' + (err.response?.data?.message || err.message));
    }
  };

  if (loading) return <div style={{ padding: embedded ? 0 : 24 }}>Chargement…</div>;

  return (
    <div style={{ padding: embedded ? 0 : 24, maxWidth: 1100 }}>
      {/* Header - hidden when embedded (pagina parent are deja h1) */}
      {!embedded && <h1 style={{ marginBottom: 16, fontSize: 24 }}>Mes congés</h1>}

      {error && (
        <div style={{ padding: 12, background: '#FEE2E2', color: '#991B1B', borderRadius: 6, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* === SECTION 1: Soldes === */}
      {balance && (
        <section style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 16, marginBottom: 8 }}>
            Mes soldes — année {balance.year}
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {balance.quotas.filter((q) => q.totalAllocated > 0 || q.used > 0 || (q.pending || 0) > 0).map((q) => {
              const hasPending = (q.pending || 0) > 0;
              const reste = q.resteAProgrammer ?? (q.totalAllocated - q.used - (q.pending || 0));
              const encoded = q.encoded ?? q.used;
              const usedPast = q.usedPast ?? 0;
              const aVenir = q.aVenir ?? Math.max(0, q.used - usedPast);
              const positive = reste > 0;
              return (
                <div key={q.type} style={{
                  background: positive ? '#FAEEDA' : '#F1F5F9',
                  color: positive ? '#633806' : '#64748B',
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: '1px solid ' + (positive ? '#E8D4B0' : '#CBD5E1'),
                  minWidth: 200,
                }} title={LEAVE_TYPE_LABELS[q.type]}>
                  <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>{q.type}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.1 }}>
                    {reste}<span style={{ opacity: 0.55, fontWeight: 400, fontSize: 14 }}> / {q.totalAllocated}</span> j
                  </div>
                  <div style={{ fontSize: 9, opacity: 0.6, marginTop: 1, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    reste à programmer
                  </div>
                  <div style={{ borderTop: '1px solid ' + (positive ? '#E8D4B0' : '#CBD5E1'), paddingTop: 5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div style={{ fontSize: 10.5, display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ opacity: 0.75 }}>Encodé</span>
                      <strong>{encoded} j</strong>
                    </div>
                    <div style={{ fontSize: 10, display: 'flex', justifyContent: 'space-between', opacity: 0.75, paddingLeft: 8 }}>
                      <span>├ Utilisé <span style={{ opacity: 0.6 }}>(passé)</span></span>
                      <span><strong>{usedPast}</strong> j</span>
                    </div>
                    <div style={{ fontSize: 10, display: 'flex', justifyContent: 'space-between', opacity: 0.75, paddingLeft: 8 }}>
                      <span>└ À venir <span style={{ opacity: 0.6 }}>(programmé)</span></span>
                      <span><strong>{aVenir}</strong> j</span>
                    </div>
                    <div style={{ fontSize: 10.5, display: 'flex', justifyContent: 'space-between', color: hasPending ? '#92400e' : 'inherit', fontWeight: hasPending ? 600 : 'normal' }}>
                      <span style={{ opacity: hasPending ? 1 : 0.6 }}>En attente</span>
                      <strong>{q.pending || 0} j</strong>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* === SECTION 2: Nouvelle demande === */}
      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>Nouvelle demande</h2>
        <div style={{
          background: 'white',
          border: '1px solid #E2E8F0',
          borderRadius: 8,
          padding: 20,
          maxWidth: 720,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <Field label="TYPE">
              <select value={formType} onChange={e => setFormType(e.target.value)} style={inputStyle} disabled={submitting}>
                {Object.entries(LEAVE_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{k} — {v}</option>
                ))}
              </select>
            </Field>
            {/* Day 11 — REC + AA: toggle jours/heures + (REC seulement) solde OT */}
            {supportsHoursMode && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', border: '1px solid #cbd5e1', borderRadius: 8, overflow: 'hidden' }}>
                  <div onClick={() => !submitting && setRecMode('jours')} style={{ padding: '7px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', background: recMode === 'jours' ? '#15803d' : '#fff', color: recMode === 'jours' ? '#fff' : '#64748b' }}>📅 Jours</div>
                  <div onClick={() => !submitting && setRecMode('heures')} style={{ padding: '7px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', background: recMode === 'heures' ? '#15803d' : '#fff', color: recMode === 'heures' ? '#fff' : '#64748b' }}>⏱️ Heures</div>
                </div>
                {formType === 'REC' && otBalance !== null && (
                  <span style={{ fontSize: 12, padding: '3px 8px', borderRadius: 6, background: otBalance > 0 ? '#ecfdf5' : '#fef2f2', color: otBalance > 0 ? '#065f46' : '#991b1b', fontWeight: 600 }}>
                    Solde h. sup. : {Math.floor(Math.abs(otBalance))}h{String(Math.round((Math.abs(otBalance) % 1) * 60)).padStart(2, '0')}
                  </span>
                )}
              </div>
            )}
            <Field label="DATE DE DÉBUT">
              <input type="date" value={formStart} onChange={e => setFormStart(e.target.value)} style={inputStyle} disabled={submitting} />
            </Field>
            {supportsHoursMode && recMode === 'heures' ? (
              <Field label="DURÉE (hh:mm)">
                <input type="text" value={recHours} onChange={e => setRecHours(e.target.value)} placeholder={formType === 'AA' ? 'Ex: 1:00' : 'Ex: 2:24'} style={inputStyle} disabled={submitting} />
              </Field>
            ) : (
              <Field label="DATE DE FIN">
                <input type="date" value={formEnd} onChange={e => setFormEnd(e.target.value)} style={inputStyle} disabled={submitting} />
              </Field>
            )}
            <Field label="MOTIF (optionnel)">
              <input type="text" value={formReason} onChange={e => setFormReason(e.target.value)} placeholder="Ex: voyage en famille" style={inputStyle} maxLength={500} disabled={submitting} />
            </Field>
          </div>

          {feedback && (
            <div style={{
              padding: 12, marginBottom: 12, borderRadius: 6, fontSize: 13,
              background: feedback.type === 'success' ? '#DCFCE7' : '#FEE2E2',
              color: feedback.type === 'success' ? '#166534' : '#991B1B',
              border: '1px solid ' + (feedback.type === 'success' ? '#86EFAC' : '#FCA5A5'),
            }}>
              {feedback.message}
            </div>
          )}

          <button onClick={handleSubmit} disabled={submitting} style={{
            padding: '10px 24px', background: submitting ? '#94a3b8' : '#15803d',
            color: 'white', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 500,
            cursor: submitting ? 'wait' : 'pointer',
          }}>
            {submitting ? 'Envoi…' : 'Soumettre la demande'}
          </button>
        </div>
      </section>

      {/* === SECTION 3: Historique === */}
      <section>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>Historique de mes demandes</h2>
        {leavesList.length === 0 ? (
          <div style={{ padding: 24, background: '#F8FAFC', borderRadius: 6, textAlign: 'center', color: '#64748B' }}>
            Aucune demande pour le moment.
          </div>
        ) : isMobile ? (
          /* Mobile : carduri verticale */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {leavesList.map(l => {
              const sc = STATUS_COLORS[l.status];
              const sameDay = l.startDate === l.endDate;
              return (
                <div key={l.id} style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
                        {l.type}
                        {l.partialDurationMin && (
                          <span style={{ marginLeft: 6, fontSize: 11, color: '#7c3aed' }}>
                            ⏱ {Math.floor(l.partialDurationMin / 60)}h{String(l.partialDurationMin % 60).padStart(2, '0')}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                        {formatDateFR(l.startDate)}{!sameDay && ` → ${formatDateFR(l.endDate)}`}
                      </div>
                    </div>
                    <span style={{ background: sc.bg, color: sc.fg, padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600, border: '1px solid ' + sc.border, whiteSpace: 'nowrap' }}>
                      {STATUS_LABELS[l.status]}
                    </span>
                  </div>
                  {l.reason && (
                    <div style={{ fontSize: 12, color: '#475569', marginBottom: 6 }}>
                      <strong>Motif :</strong> {l.reason}
                    </div>
                  )}
                  {l.decidedAt && (
                    <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6 }}>
                      Décidé le {formatDateFR(l.decidedAt.slice(0, 10))}
                      {l.decidedReason && <>: <em>{l.decidedReason}</em></>}
                    </div>
                  )}
                  {l.status === 'PENDING' && (
                    <button onClick={() => handleCancel(l.id)} style={{
                      width: '100%', marginTop: 4, padding: 10,
                      background: '#FEE2E2', color: '#991B1B',
                      border: '1px solid #FCA5A5', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}>Annuler ma demande</button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ background: '#F8FAFC' }}>
                <tr>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Du</th>
                  <th style={thStyle}>Au</th>
                  <th style={thStyle}>Motif</th>
                  <th style={thStyle}>Statut</th>
                  <th style={thStyle}>Décision</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leavesList.map(l => {
                  const sc = STATUS_COLORS[l.status];
                  return (
                    <tr key={l.id} style={{ borderTop: '1px solid #E2E8F0' }}>
                      <td style={tdStyle}><strong>{l.type}</strong></td>
                      <td style={tdStyle}>{formatDateFR(l.startDate)}</td>
                      <td style={tdStyle}>{formatDateFR(l.endDate)}</td>
                      <td style={tdStyle}>{l.reason || '—'}</td>
                      <td style={tdStyle}>
                        <span style={{ background: sc.bg, color: sc.fg, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, border: '1px solid ' + sc.border }}>
                          {STATUS_LABELS[l.status]}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        {l.decidedAt ? formatDateFR(l.decidedAt.slice(0, 10)) : '—'}
                        {l.decidedReason && <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>{l.decidedReason}</div>}
                      </td>
                      <td style={tdStyle}>
                        {l.status === 'PENDING' && (
                          <button onClick={() => handleCancel(l.id)} style={{
                            padding: '4px 10px', background: '#FEE2E2', color: '#991B1B',
                            border: '1px solid #FCA5A5', borderRadius: 4, fontSize: 11, cursor: 'pointer',
                          }}>Annuler</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #CBD5E1',
  borderRadius: 6,
  fontSize: 14,
  fontFamily: 'inherit',
  background: 'white',
};

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 500,
  color: '#64748B',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const tdStyle = { padding: '8px 12px' };

function Field({ label, children }) {
  return (
    <div>
      <label style={{
        display: 'block', fontSize: 11, fontWeight: 500, color: '#64748B',
        marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px',
      }}>{label}</label>
      {children}
    </div>
  );
}
